import { useEffect, useState } from "react";
import {
	History,
	Loader2,
	AlertCircle,
	ChevronLeft,
	ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { useApiSWR } from "@/hooks/useApiSWR";
import {
	getTaskHistory,
	type TaskHistoryList,
} from "@/services/task-history.service";
import type { ActivityEntry } from "@/services/activity.service";
import { avatarColor } from "@/components/task-page/utils";
import { describeChanges } from "./describe-change";

const PAGE_SIZE = 5;

function getInitials(name: string | null | undefined): string {
	if (!name) return "?";
	return name
		.split(" ")
		.map((w) => w[0])
		.filter(Boolean)
		.slice(0, 2)
		.join("")
		.toUpperCase();
}

function formatRelative(iso: string): string {
	const then = new Date(iso).getTime();
	const diff = Math.round((then - Date.now()) / 1000);
	const abs = Math.abs(diff);
	const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
	if (abs < 60) return rtf.format(Math.round(diff), "second");
	if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
	if (abs < 86400) return rtf.format(Math.round(diff / 3600), "hour");
	if (abs < 604800) return rtf.format(Math.round(diff / 86400), "day");
	return new Date(iso).toLocaleDateString();
}

function formatExact(iso: string): string {
	return new Date(iso).toLocaleString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}

function HistoryRow({ entry }: { entry: ActivityEntry }) {
	const actorName = entry.actor?.full_name ?? entry.actor?.email ?? "Someone";
	const initials = getInitials(entry.actor?.full_name ?? entry.actor?.email);
	const lines = describeChanges(entry);

	return (
		<li className="flex items-start gap-3 py-3">
			<Avatar className="h-7 w-7 shrink-0">
				<AvatarImage
					src={entry.actor?.avatar_url ?? undefined}
					alt={actorName}
				/>
				<AvatarFallback
					className={`text-white text-xs ${avatarColor(String(entry.actor?.full_name)?.length)}`}
				>
					{initials}
				</AvatarFallback>
			</Avatar>
			<div className="flex-1 min-w-0">
				<div className="text-sm leading-relaxed">
					<span className="font-medium">{actorName}</span>{" "}
					<span className="text-muted-foreground">
						{lines.join("; ")}
					</span>
				</div>
				<time
					className="text-xs text-muted-foreground"
					dateTime={entry.created_at}
				>
					{formatExact(entry.created_at)}
				</time>
			</div>
			<time
				className="text-xs text-muted-foreground whitespace-nowrap pt-0.5"
				dateTime={entry.created_at}
				title={new Date(entry.created_at).toLocaleString()}
			>
				{formatRelative(entry.created_at)}
			</time>
		</li>
	);
}

interface TaskHistorySectionProps {
	projectId: string;
	taskId: string;
	/** Changes when the task is mutated, triggering a refetch. */
	refreshKey?: string;
}

export function TaskHistorySection({
	projectId,
	taskId,
	refreshKey,
}: TaskHistorySectionProps) {
	const [page, setPage] = useState(1);

	// Each page is a distinct SWR cache entry, so only 5 rows are ever fetched
	// and rendered at a time. keepPreviousData holds the current page's rows on
	// screen while the next loads, avoiding a skeleton flash on navigation.
	const {
		data,
		error: loadError,
		isLoading: loading,
		isValidating,
		mutate,
	} = useApiSWR<TaskHistoryList>(
		["tasks", taskId, "history", page],
		() => getTaskHistory(projectId, taskId, { page, limit: PAGE_SIZE }),
		{ keepPreviousData: true },
	);

	// Reset to the first page when the task changes or it is mutated.
	const [resetMarker, setResetMarker] = useState({ refreshKey, taskId });
	if (resetMarker.refreshKey !== refreshKey || resetMarker.taskId !== taskId) {
		setResetMarker({ refreshKey, taskId });
		setPage(1);
	}

	useEffect(() => {
		mutate();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refreshKey, taskId]);

	const entries = data?.data ?? [];
	const totalPages = data?.totalPages ?? 1;
	const currentPage = data?.page ?? page;
	const error = loadError?.message ?? null;

	return (
		<section>
			<div className="flex items-center gap-2 mb-3">
				<History className="h-4 w-4 text-muted" />
				<h3 className="text-sm font-semibold text-foreground">
					History
				</h3>
			</div>

			{loading ? (
				<div className="space-y-3">
					{Array.from({ length: 3 }).map((_, i) => (
						<div key={i} className="flex items-start gap-3">
							<Skeleton className="h-7 w-7 rounded-full" />
							<div className="flex-1 space-y-2">
								<Skeleton className="h-3 w-2/3" />
								<Skeleton className="h-3 w-1/4" />
							</div>
						</div>
					))}
				</div>
			) : error ? (
				<div className="flex flex-col items-center gap-2 py-6 text-center">
					<AlertCircle className="h-5 w-5 text-danger" />
					<p className="text-sm text-muted-foreground">{error}</p>
					<Button variant="outline" size="sm" onClick={() => mutate()}>
						Try again
					</Button>
				</div>
			) : entries.length === 0 ? (
				<p className="text-xs text-muted italic py-2">No history yet.</p>
			) : (
				<>
					<ul className="divide-y divide-border">
						{entries.map((e) => (
							<HistoryRow key={e.id} entry={e} />
						))}
					</ul>
					{totalPages > 1 && (
						<div className="pt-3 flex items-center justify-between">
							<Button
								variant="outline"
								size="sm"
								className="h-7 px-2 text-xs"
								disabled={currentPage <= 1 || isValidating}
								onClick={() =>
									setPage((p) => Math.max(1, p - 1))
								}
							>
								<ChevronLeft className="h-3 w-3 mr-1" />
								Prev
							</Button>
							<span className="text-xs text-muted-foreground flex items-center gap-1.5">
								{isValidating && (
									<Loader2 className="h-3 w-3 animate-spin" />
								)}
								Page {currentPage} of {totalPages}
							</span>
							<Button
								variant="outline"
								size="sm"
								className="h-7 px-2 text-xs"
								disabled={
									currentPage >= totalPages || isValidating
								}
								onClick={() => setPage((p) => p + 1)}
							>
								Next
								<ChevronRight className="h-3 w-3 ml-1" />
							</Button>
						</div>
					)}
				</>
			)}
		</section>
	);
}

export default TaskHistorySection;
